import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  continueAdeSetup,
  enrollBannerUrl,
  enrollProfileUrl,
  getEnrollSplash,
  loginDirectoryEnrollment,
} from "../services/enrollService";
import { renderSimpleMarkdown } from "../utils/simpleMarkdown";

function hintFromParams(params) {
  return {
    serialNumber: params.get("serial") || params.get("SERIAL") || "",
    udid: params.get("udid") || params.get("UDID") || "",
  };
}

const STORED_DEVICE_KEY = "xavierEnrollDevice";

function storedDevice() {
  try {
    return JSON.parse(sessionStorage.getItem(STORED_DEVICE_KEY) || "null");
  } catch {
    return null;
  }
}

function rememberDevice(device) {
  if (!device?.serialNumber && !device?.udid) return;
  sessionStorage.setItem(STORED_DEVICE_KEY, JSON.stringify({
    serialNumber: device.serialNumber || "",
    udid: device.udid || "",
  }));
  const url = new URL(window.location.href);
  let changed = false;
  if (device.serialNumber && !url.searchParams.get("serial")) {
    url.searchParams.set("serial", device.serialNumber);
    changed = true;
  }
  if (changed) window.history.replaceState({}, "", `${url.pathname}${url.search}`);
}

function deviceLabel(device) {
  if (!device) return "dieses Gerät";
  if (device.plannedDeviceName) return device.plannedDeviceName;
  if (device.assetTag) return `Inventar ${device.assetTag}`;
  return "dieses Gerät";
}

function assignmentToast(result) {
  const setup = result?.continued ? " Das Setup kann weitergehen." : "";
  if (result?.locationMoved && result?.locationName) {
    return `Gerät zugeordnet und nach ${result.locationName} verschoben.${setup}`;
  }
  return result?.continued ? "Gerät zugeordnet. Das Setup kann weitergehen." : "Gerät zugeordnet.";
}

function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOs = ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document;
  return iOS || iPadOs;
}

export default function Enroll() {
  const [params] = useSearchParams();
  const [splash, setSplash] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [device, setDevice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [directoryToken, setDirectoryToken] = useState(() => (
    typeof sessionStorage !== "undefined" ? sessionStorage.getItem("enrollDirectoryToken") || "" : ""
  ));
  const [showBanner, setShowBanner] = useState(false);
  const [bannerLocationId, setBannerLocationId] = useState("");
  const [bannerRev, setBannerRev] = useState(0);

  const profileUuid = params.get("profile") || "";
  const queryHint = hintFromParams(params);
  const remembered = (queryHint.serialNumber || queryHint.udid) ? storedDevice() : null;

  useEffect(() => {
    let cancelled = false;
    const applySplash = (data) => {
      setSplash(data);
      setShowBanner(Boolean(data.hasBanner));
      if (data.device?.serialNumber || data.device?.udid) {
        setDevice(data.device);
        rememberDevice(data.device);
      }
      if (data.setupContinued && data.assigned) {
        setDone({
          ok: true,
          continued: true,
          user: data.assignedUser,
          device: data.device,
        });
      }
    };
    getEnrollSplash({
      profile: profileUuid,
      serial: queryHint.serialNumber,
      udid: queryHint.udid,
    })
      .then((data) => {
        if (!cancelled) applySplash(data);
      })
      .catch(() => {
        if (!cancelled) {
          setSplash({ markdown: "", enabled: true });
          setShowBanner(false);
        }
      });
    return () => { cancelled = true; };
  }, [profileUuid, queryHint.serialNumber, queryHint.udid]);

  useEffect(() => {
    if (done?.continued) return undefined;
    if (!splash?.assigned || splash?.setupContinued) return undefined;
    if (device?.udid && splash?.canContinueSetup) return undefined;
    const timer = setInterval(() => {
      getEnrollSplash({
        profile: profileUuid,
        serial: device?.serialNumber || queryHint.serialNumber,
        udid: device?.udid || queryHint.udid,
      }).then((data) => {
        setSplash(data);
        if (data.device?.serialNumber || data.device?.udid) {
          setDevice(data.device);
          rememberDevice(data.device);
        }
        if (data.setupContinued && data.assigned) {
          setDone({
            ok: true,
            continued: true,
            user: data.assignedUser,
            device: data.device,
          });
        }
      }).catch(() => {});
    }, 2500);
    return () => clearInterval(timer);
  }, [done?.continued, splash?.assigned, splash?.setupContinued, splash?.canContinueSetup, device?.udid, device?.serialNumber, profileUuid, queryHint.serialNumber, queryHint.udid]);

  const html = useMemo(() => renderSimpleMarkdown(splash?.markdown || ""), [splash]);
  const bannerSrc = enrollBannerUrl({
    profile: profileUuid,
    serial: device?.serialNumber || queryHint.serialNumber,
    udid: device?.udid || queryHint.udid,
    location: bannerLocationId,
    rev: bannerRev,
  });

  const applyAssignmentBanner = (result, user) => {
    const loc = result?.user?.location || user?.location;
    const locationId = loc?._id || loc?.id || (typeof loc === "string" ? loc : "");
    if (locationId) setBannerLocationId(String(locationId));
    setShowBanner(true);
    setBannerRev((value) => value + 1);
  };

  const continueSetup = async () => {
    setBusy(true);
    try {
      const data = await continueAdeSetup({
        serialNumber: device?.serialNumber || queryHint.serialNumber || remembered?.serialNumber,
        udid: device?.udid || queryHint.udid || remembered?.udid,
        profile: profileUuid,
      });
      setDone({
        ok: true,
        continued: Boolean(data.continued),
        user: data.user || splash?.assignedUser,
        device: data.device || device,
      });
      toast.success(data.continued
        ? "Setup wird fortgesetzt. Bitte dieses Fenster nicht schließen."
        : "Gerät ist zugeordnet.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Setup konnte nicht fortgesetzt werden.");
    } finally {
      setBusy(false);
    }
  };

  const login = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const knownSerial = device?.serialNumber || queryHint.serialNumber || remembered?.serialNumber;
      const knownUdid = device?.udid || queryHint.udid || remembered?.udid;
      const data = await loginDirectoryEnrollment({
        username,
        password,
        serialNumber: knownSerial,
        udid: knownUdid,
        profile: profileUuid,
      });
      if (data?.token) setDirectoryToken(data.token);
      if (data.device) {
        setDevice(data.device);
        rememberDevice(data.device);
      }
      if (data.assigned?.ok) {
        setDone(data.assigned);
        applyAssignmentBanner(data.assigned, data.user);
        toast.success(assignmentToast(data.assigned));
        return;
      }
      setDone({
        ok: true,
        continued: false,
        manual: true,
        user: data.user,
        device: data.device || null,
      });
      toast.success("Anmeldung erfolgreich.");
      const profileHref = enrollProfileUrl(data.token);
      if (isIosSafari() && profileHref) {
        window.location.assign(profileHref);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Anmeldung oder Zuordnung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container py-4" style={{ maxWidth: 640 }}>
      {showBanner ? (
        <img
          alt={splash?.bannerLocationName || "Enrollment"}
          src={bannerSrc}
          className="img-fluid rounded mb-3"
          style={{ width: "100%", maxHeight: 180, objectFit: "cover" }}
          onError={() => setShowBanner(false)}
        />
      ) : null}
      <h1 className="h3 mb-3">{splash?.profileName || (device ? "Geräte-Einrichtung" : "Gerät manuell registrieren")}</h1>
      {html ? (
        <div className="mb-4" dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <p className="text-muted">
          {device
            ? "Bitte mit dem Schul- oder Organisationskonto anmelden, um dieses Gerät zuzuordnen."
            : "Bitte mit dem Schul- oder Organisationskonto anmelden. Ein Gerät aus dem Setup-Assistenten wird nur zugeordnet, wenn es hier erkannt wird."}
        </p>
      )}

      {done?.ok ? (
        <div className="alert alert-success">
          {done.manual && !done.device ? (
            <>
              <p className="mb-1">Angemeldet als <strong>{done.user?.name}</strong>.</p>
              <p className="mb-0">Als Nächstes das MDM-Profil auf diesem Gerät installieren. Das ist eine manuelle Registrierung ohne ADE: das Gerät bleibt unbetreut (nicht supervised).</p>
            </>
          ) : (
            <>
              <p className="mb-1"><strong>{done.user?.name}</strong> ist {deviceLabel(done.device || device)} zugeordnet.</p>
              <p className="mb-0">
                {done.continued
                  ? "Bitte dieses Fenster nicht schließen. Der Setup-Assistent fährt selbst fort, sobald das iPad den MDM-Befehl abholt."
                  : "Installieren Sie als Nächstes das MDM-Profil auf diesem Gerät, falls es noch nicht über ADE eingeschrieben ist."}
                {done.locationMoved && done.locationName ? ` Standort: ${done.locationName}.` : ""}
              </p>
            </>
          )}
          {(directoryToken || (typeof sessionStorage !== "undefined" && sessionStorage.getItem("enrollDirectoryToken"))) ? (
            <div className="mt-3">
              <a className="btn btn-primary" href={enrollProfileUrl(directoryToken)}>
                MDM-Profil installieren
              </a>
              <p className="form-text mb-0 mt-2">
                Safari öffnet das Konfigurationsprofil. Unter Einstellungen → Allgemein → VPN und Geräteverwaltung
                das heruntergeladene Profil erlauben. Danach ist das Gerät unbetreut im MDM.
              </p>
            </div>
          ) : null}
        </div>
      ) : splash?.assigned ? (
        <div className="card card-body">
          <p className="mb-2">
            <strong>{splash.assignedUser?.name || "Ein Benutzer"}</strong> ist {deviceLabel(device)} bereits zugeordnet.
          </p>
          {splash.awaitingEnrollment || !device?.udid ? (
            <p className="text-muted mb-3">
              Das Gerät ist noch nicht beim MDM gemeldet. Bitte die Remote-Verwaltung im Setup-Assistenten aktivieren
              und diese Seite offen lassen. Sobald das Gerät verbunden ist, kann das Setup fortgesetzt werden.
            </p>
          ) : (
            <p className="text-muted mb-3">
              Jetzt das Setup fortsetzen, damit der Assistent nach der Onboarding-Seite weiterläuft.
            </p>
          )}
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || (!device?.udid && !splash.canContinueSetup)}
            onClick={continueSetup}
          >
            {busy ? "Bitte warten…" : "Setup fortsetzen"}
          </button>
        </div>
      ) : (
        <form onSubmit={login} className="card card-body">
          {device ? (
            <p className="mb-3">
              Nach der Anmeldung wird <strong>{deviceLabel(device)}</strong> automatisch zugeordnet.
              Es ist keine Seriennummer nötig.
            </p>
          ) : (
            <p className="mb-3">
              Nach der Anmeldung können Sie sich als Gerätebenutzer registrieren.
              Ein wartendes ADE-Gerät wird hier nicht automatisch ausgewählt.
            </p>
          )}
          <label className="form-label">Benutzername oder E-Mail</label>
          <input className="form-control mb-2" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} />
          <label className="form-label">Passwort</label>
          <input className="form-control mb-3" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Anmelden…" : (device ? "Anmelden und Gerät zuordnen" : "Anmelden")}
          </button>
        </form>
      )}
    </div>
  );
}
